import * as Sentry from '@sentry/react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {keepPreviousData, useQueryClient} from '@tanstack/react-query';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {usePartner__listDocumentsToCheck} from '../../../../../generated/blueprint.ts';
import {QueryKey} from '../../../../../api/queryKeys.ts';
import {User} from '../../../../../api/accessControl/User.ts';
import {BackendApi} from '../../../../../api/api.ts';
import Shell from '../../../../../components/specific/Shell.tsx';
import {usePartnerClientScope} from '../../../../../hooks/usePartnerClientScope.ts';
import {ClientSubtitle} from '../../../../../components/specific/ClientSwitcher';
import {PartnerClientToolbar, getVisibleGoToItemIds} from '../../../../../components/specific/PartnerClientToolbar';
import {useFilterStore} from '../../../../../contexts/FilterStore.tsx';
import {useNotifications} from '../../../../../lib/NotificationContext.tsx';
import {useDocumentDrawer, useOpenDocument} from '../../../../../views/DocumentDrawer';
import DocumentsToCheckFilterBar from './DocumentsToCheckFilterBar.tsx';
import {DocumentsToCheckTable} from './DocumentsToCheckTable.tsx';
import {OutsidePeriodBanner} from './OutsidePeriodBanner.tsx';
import PossibleDuplicatesModal from '../../../../../components/specific/PossibleDuplicates/PossibleDuplicatesModal.tsx';
import {BILL_DUPLICATES_QUERY} from '../../../../../hooks/useBillDuplicates.ts';
import {usePossibleDuplicates} from '../../../../../hooks/usePossibleDuplicates.ts';
import {
    CAN_SEE_DOCUMENT_TODOS_FLAG,
    DEFAULT_SORTING,
    DocumentToCheck,
    DocumentsToCheckFilters,
    DocumentsToCheckSorting,
    emptyToNull,
    periodParams,
} from './documentsToCheck.ts';

const DRAWER_AUTO_ADVANCE = {onSave: true, onRecategorize: true, onArchive: true};
const NO_DOCUMENTS: DocumentToCheck[] = [];

export default function PartnerDocumentsToCheckPage({currentUser}: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const {state, dispatch} = useFilterStore();
    const {dispatch: notifyDispatch} = useNotifications();
    const documentDrawer = useDocumentDrawer();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const {documentId: routeDocumentId} = useParams();
    const openDocumentId = useOpenDocument()?.documentId ?? null;
    const openedFromRoute = useRef(false);
    const drawerWasOpen = useRef(false);

    const [sorting, setSorting] = useState<DocumentsToCheckSorting>(DEFAULT_SORTING);

    const clientScope = usePartnerClientScope(currentUser);

    const filters = useMemo<DocumentsToCheckFilters>(() => ({
        dateFrom: state.common.dateRange.from,
        dateTo: state.common.dateRange.to,
        dateType: state.documentsToCheck.dateType,
        ignoreDateRange: state.documentsToCheck.ignoreDateRange,
        customerId: clientScope.selectedClientId ?? '',
        reasons: state.documentsToCheck.reasons,
        todoTypes: state.documentsToCheck.todoTypes,
    }), [
        state.common.dateRange.from,
        state.common.dateRange.to,
        clientScope.selectedClientId,
        state.documentsToCheck.dateType,
        state.documentsToCheck.ignoreDateRange,
        state.documentsToCheck.reasons,
        state.documentsToCheck.todoTypes,
    ]);

    const documentsQuery = usePartner__listDocumentsToCheck(api, {
        ...periodParams(filters),
        date_type: filters.dateType,
        business_ids: emptyToNull(filters.customerId),
        reasons: filters.reasons.length > 0 ? filters.reasons.join(',') : null,
        todo_types: filters.todoTypes.length > 0 ? filters.todoTypes.join(',') : null,
        orderby: sorting.orderby,
        order: sorting.order,
    }, {
        queryKey: QueryKey.Documents,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        select: (response) => ({
            documents: response.documents as unknown as DocumentToCheck[],
            outsidePeriodCount: response.outside_period_count,
        }),
    });

    const documents = documentsQuery.data?.documents ?? NO_DOCUMENTS;
    const outsidePeriodCount = documentsQuery.data?.outsidePeriodCount ?? 0;

    const showDocumentsOutsidePeriod = useCallback(
        () => dispatch({type: 'SET_FILTERS', payload: {documentsToCheck: {ignoreDateRange: true}}}),
        [dispatch],
    );

    const refreshDocuments = useCallback(
        () => queryClient.invalidateQueries({queryKey: [QueryKey.Documents]}),
        [queryClient],
    );
    const possibleDuplicates = usePossibleDuplicates(api, {
        openDocument: documentDrawer.open,
        onDeleted: refreshDocuments,
        closeOnView: true,
    });

    useEffect(() => {
        if (openDocumentId) {
            drawerWasOpen.current = true;
            return;
        }
        if (drawerWasOpen.current) {
            drawerWasOpen.current = false;
            void refreshDocuments();
            void queryClient.invalidateQueries({queryKey: [BILL_DUPLICATES_QUERY]});
        }
    }, [openDocumentId, refreshDocuments, queryClient]);

    const drawerDocuments = useMemo(
        () => documents.map((document) => ({
            id: document.id,
            businessId: document.business_id,
            fileName: document.file_name,
        })),
        [documents],
    );

    useEffect(() => {
        if (openedFromRoute.current || documentsQuery.isPending || documentsQuery.isError || !routeDocumentId) {
            return;
        }

        const document = documents.find((candidate) => candidate.id === routeDocumentId);
        if (document) {
            documentDrawer.open(document.business_id, document.id, {
                documentList: drawerDocuments,
                autoAdvance: DRAWER_AUTO_ADVANCE,
            });
        }
        openedFromRoute.current = true;
    }, [
        documentsQuery.isPending,
        documentsQuery.isError,
        routeDocumentId,
        documents,
        drawerDocuments,
        documentDrawer,
    ]);

    useEffect(() => {
        if (!openDocumentId && routeDocumentId && !openedFromRoute.current) {
            return;
        }

        const target = openDocumentId ? `/documents-to-check/${openDocumentId}` : '/documents-to-check';
        if (location.pathname === target) {
            return;
        }
        navigate(target, {replace: true});
    }, [
        openDocumentId,
        routeDocumentId,
        location.pathname,
        navigate,
    ]);

    const handleDelete = useCallback(async (document: DocumentToCheck) => {
        try {
            await api.Partner__client__document__delete({document_id: document.id});
            await refreshDocuments();
            notifyDispatch({
                title: 'Document verwijderd',
                description: 'Het document is succesvol verwijderd',
                type: 'success',
            });
        } catch (error) {
            Sentry.captureException(error);
            notifyDispatch({
                title: 'Fout bij verwijderen',
                description: 'Er is een fout opgetreden bij het verwijderen van het document. Probeer het later opnieuw.',
                type: 'error',
            });
        }
    }, [api, refreshDocuments, notifyDispatch]);

    return (
        <Shell
            currentUser={currentUser}
            title="Te controleren"
            subtitle={<ClientSubtitle prefix="Te controleren documenten van" clientName={clientScope.selectedClient?.name ?? null} fallback="Documenten die nog aandacht nodig hebben voordat ze in de administratie komen"/>}
            titleActions={
                <PartnerClientToolbar
                    clients={clientScope.clients}
                    selectedClientId={clientScope.selectedClientId}
                    recentClientIds={clientScope.recentClientIds}
                    goToItemIds={getVisibleGoToItemIds((flag) => currentUser.flags.getFlag(flag))}
                    onSelectClient={clientScope.selectClient}
                />
            }
        >
            <div className="w-full">
                {documentsQuery.isError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd"
                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                          clipRule="evenodd"/>
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">
                                    Er zijn geen documenten beschikbaar of u heeft niet genoeg rechten om ze te
                                    bekijken.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg">
                    <DocumentsToCheckFilterBar canSeeTodos={currentUser.flags.getFlag(CAN_SEE_DOCUMENT_TODOS_FLAG)}/>
                    <OutsidePeriodBanner
                        count={outsidePeriodCount}
                        onShowAll={showDocumentsOutsidePeriod}
                    />
                    <DocumentsToCheckTable
                        documents={documents}
                        isLoading={documentsQuery.isPending || documentsQuery.isPlaceholderData}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        onDelete={handleDelete}
                        onRowClick={(documentId, businessId) =>
                            documentDrawer.open(businessId, documentId, {
                                documentList: drawerDocuments,
                                autoAdvance: DRAWER_AUTO_ADVANCE,
                            })
                        }
                        onPossibleDuplicateClick={possibleDuplicates.open}
                    />
                    <PossibleDuplicatesModal {...possibleDuplicates.modalProps}/>
                </div>
            </div>
        </Shell>
    );
}
